import React, { useEffect, useState } from 'react';
import {
  AppRemixIcon,
  AppSingleSelect,
  AppNoInfo,
} from '@/layout/AppComponents';
import styled from 'styled-components';
import { Scrollbars } from 'react-custom-scrollbars';
import axios from 'axios';
import { useParams } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import ReactDOMServer from 'react-dom/server';
import { axiosPath } from '../../../../../../routing/helpers/router';
import colors from '../../../../../../constants/colors';
import minions from '../../../../../../minions/minions';
import * as currentPageAction from '../../../../../../store/actions/currentPageAction';
import NotificationAlert from '../../../../../components/helpers/notificationAlert';
import Translator from '../../../../../components/helpers/Translator';
import PlantEvidenceNewForm from './PlantEvidenceNewForm';
import PlantEvidenceEditForm from "./PlantEvidenceEditForm";
import errorHandler from '@/layout/components/error/ErrorHandler';

const PlantEvidence = (props) => {
  const { evidenceFolderList } = props;
  const { moduleBridgeHex, plantHex } = useParams();
  const dispatch = useDispatch();
  const userId = useSelector((s) => s?.auth?.user?.id);
  const userBridgeSettings = useSelector(
    (s) => s.module?.[moduleBridgeHex]?.userBridge?.permissions
  );
  const evidencesPermissions = useSelector(
    (s) =>
      s.module?.[moduleBridgeHex]?.permissions?.gcl?.module?.plant?.evidences
  );
  const [evidenceFolders, setEvidenceFolders] = useState(evidenceFolderList);
  const [showAddNewEvidence, setShowAddNewEvidence] = useState(false);
  const [selectedEvidenceFolder, setSelectedEvidenceFolder] = useState();
  const [selectedEvidence, setSelectedEvidence] = useState(null);
  const [isEditMode, setIsEditMode] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    return () => {
      dispatch(currentPageAction.empty());
    };
  }, []);

  const isEditingForbidden = () =>
    (userBridgeSettings?.level === 2 &&
      !userBridgeSettings?.evidences?.write) ||
    (userId !== selectedEvidence.userId &&
      evidencesPermissions?.editOnlyByUser &&
      evidencesPermissions?.editOnlyByUser !== false &&
      (!evidencesPermissions?.editOnlyByUser?.editOnlyByUserExceptions?.users ||
        !evidencesPermissions?.editOnlyByUser?.editOnlyByUserExceptions?.users.includes(
          userId
        )));

  useEffect(() => {
    if (selectedEvidenceFolder) {
      const updatedEvidenceFolder = evidenceFolders.find(
        (ef) => ef.id === selectedEvidenceFolder.id
      );
      setSelectedEvidenceFolder(updatedEvidenceFolder);
      if (selectedEvidence)
        setSelectedEvidence(
          updatedEvidenceFolder.evidences.find(
            (e) => e.id === selectedEvidence.id
          )
        );
    } else {
      setSelectedEvidenceFolder(evidenceFolders[0]);
    }
  }, [evidenceFolders]);

  const handleDownloadFile = async (url = null) => {
    try {
      const response = await axios.post(
        axiosPath(
          "api_r_user_module_legalCompliance_plantInformation_downloadEvidence"
        ),
        {
          evidenceID: selectedEvidence.id,
          plantHex,
          url,
        }
      );
      window.open(response.data, "_blank");
      return response.data;
    } catch (err) {}
  };

  // < ADD EVIDENCE
  const handleAddEvidence = async (values) => {
    if (isLoading) return;
    setIsLoading(true);
    try {
      const response = await axios.post(
        axiosPath(
          "api_r_user_module_legalCompliance_plantInformation_newEvidence"
        ),
        minions.forms.toFormData({
          ...values,
          plantHex,
          evidenceFolderID: selectedEvidenceFolder.id,
        }),
        { headers: { "Content-Type": "multipart/form-data" } }
      );
      setEvidenceFolders(response.data);
      setShowAddNewEvidence(false);
      setIsLoading(false);
    } catch (err) {
      setIsLoading(false);
      dispatch(errorHandler(err));
    }
  };
  // < ADD EVIDENCE

  // < EDIT EVIDENCE
  const handleEditEvidence = async (values) => {
    if (isLoading) return;
    setIsLoading(true);
    try {
      const response = await axios.post(
        axiosPath(
          "api_r_user_module_legalCompliance_plantInformation_editEvidence"
        ),
        minions.forms.toFormData({
          ...values,
          plantHex,
          evidenceID: selectedEvidence.id,
        }),
        { headers: { "Content-Type": "multipart/form-data" } }
      );
      setEvidenceFolders(response.data);
      setIsEditMode(false);
      setIsLoading(false);
      return response.data;
    } catch (err) {
      setIsLoading(false);
      dispatch(errorHandler(err));
    }
  };
  // < EDIT EVIDENCE

  // > REMOVE EVIDENCE
  const handleRemove = async () => {
    if (isLoading) return;
    setIsLoading(true);
    try {
      const response = await axios.post(
        axiosPath(
          "api_r_user_module_legalCompliance_plantInformation_removeEvidence"
        ),
        { moduleBridgeHex, plantHex, evidenceID: selectedEvidence.id }
      );
      setEvidenceFolders(response.data);
      setShowAddNewEvidence(false);
      setSelectedEvidence(false);
      setIsEditMode(false);
      setIsLoading(false);
    } catch (err) {
      setIsLoading(false);
      NotificationAlert({
        title: ReactDOMServer.renderToString(
          <Translator file="user" name="user.error" />
        ),
        message: ReactDOMServer.renderToString(
          <Translator
            file="module-legalCompliance"
            name="error.evidence.delete"
          />
        ),
        type: "danger",
        position: "top-right",
        duration: 3000,
      });
    }
  };
  // < REMOVE EVIDENCE

  const onChangeEvidenceFolder = (v) => {
    if (v) {
      setSelectedEvidenceFolder(evidenceFolders.find((ef) => ef.id === v));
      setShowAddNewEvidence(false);
      setSelectedEvidence(false);
      setIsEditMode(false);
    }
  };

  if (!selectedEvidenceFolder || !evidencesPermissions) return '';

  return (
    <PlantEvidenceContainer>
      <SidebarMenu>
        <>
          <AppSingleSelect
            title={
              <Translator
                file="module-legalCompliance"
                name="module.lc.plant-evidence.folder"
              />
            }
            icon={<AppRemixIcon icon="folder" />}
            className="form-control"
            value={selectedEvidenceFolder.id}
            options={evidenceFolders.map((ef) => ({
              value: ef.id,
              label: ef.name,
            }))}
            onChange={onChangeEvidenceFolder}
          />
          {(userBridgeSettings?.level !== 2 ||
            userBridgeSettings?.evidences?.write === true) && (
            <span
              className="add-button"
              onClick={() => {
                setShowAddNewEvidence(true);
                setSelectedEvidence(false);
                setIsEditMode(false);
              }}
            >
              <Translator
                file="module-legalCompliance"
                name="module.lc.plant-evidence.new"
              />
            </span>
          )}
          {selectedEvidenceFolder?.evidences?.length > 0 ? (
            <Scrollbars style={{ height: '365px' }}>
              <ul>
                {selectedEvidenceFolder.evidences.map((evidence) => (
                  <li
                    key={evidence.id}
                    className={
                      selectedEvidence && selectedEvidence.id === evidence.id
                        ? 'ev-selected'
                        : ''
                    }
                    onClick={() => {
                      setShowAddNewEvidence(false);
                      setSelectedEvidence(evidence);
                      setIsEditMode(false);
                    }}
                  >
                    <div className="first-row">
                      <span
                        className="name"
                        title={evidence.name || evidence.description}
                      >
                        {evidence.name || evidence.description}
                      </span>
                      <AppRemixIcon
                        className="mr-2"
                        icon="attachment-2"
                        noVariants
                        color={
                          evidence?.urls?.length > 0
                            ? colors.primary
                            : colors.grey400
                        }
                      />
                    </div>
                    <div className="second-row">
                      <span className="sr-date">{evidence.date}</span>
                      {evidencesPermissions.showUserName && (
                        <span className="sr-user mr-2">{evidence.user}</span>
                      )}
                    </div>
                  </li>
                ))}
              </ul>
            </Scrollbars>
          ) : (
            <AppNoInfo
              text={
                <Translator file="module-legalCompliance" name="no.documents" />
              }
              color={colors.primaryLight}
              icon={
                <AppRemixIcon
                  size={24}
                  icon="file-search"
                  color={colors.primaryLight}
                />
              }
            />
          )}
        </>
      </SidebarMenu>
      <Content>
        {showAddNewEvidence ? (
          <PlantEvidenceNewForm
            handleAddEvidence={handleAddEvidence}
            isLoading={isLoading}
          />
        ) : (
          <>
            {selectedEvidence ? (
              <>
                {isEditMode ? (
                  <PlantEvidenceEditForm
                    evidenceFolders={evidenceFolders}
                    selectedEvidenceFolderId={selectedEvidenceFolder.id}
                    selectedEvidence={selectedEvidence}
                    handleEditEvidence={handleEditEvidence}
                    onCancel={() => {
                      setSelectedEvidence(null);
                      setIsEditMode(false);
                    }}
                    onRemove={handleRemove}
                    isLoading={isLoading}
                  />
                ) : (
                  <>
                    <h3>
                      <Translator
                        file="module-legalCompliance"
                        name="document.information.name"
                        variables={{ name: selectedEvidence.name }}
                      />
                      <AppRemixIcon
                        icon="close"
                        className="float-right pointer mr-2"
                        size={16}
                        onClick={() => {
                          setSelectedEvidence(null);
                          setIsEditMode(false);
                        }}
                      />
                      {isEditingForbidden() ? (
                        ''
                      ) : (
                        <AppRemixIcon
                          icon="edit"
                          className="float-right pointer mr-2"
                          size={16}
                          onClick={() => setIsEditMode(true)}
                        />
                      )}
                    </h3>
                    <EvidenceInfo className="mt-4">
                      <li>
                        <span className="eventinfo-title">
                          <Translator
                            file="module-legalCompliance"
                            name="name"
                          />
                        </span>
                        <span className="eventinfo-content">
                          {selectedEvidence.name}
                        </span>
                      </li>
                      <li>
                        <span className="eventinfo-title">
                          <Translator
                            file="module-legalCompliance"
                            name="associated.files"
                          />
                        </span>
                        <span className="eventinfo-content">
                          {selectedEvidence.urls &&
                          selectedEvidence.urls.length > 0 ? (
                            selectedEvidence.urls.map((url) => (
                              <FileDownload
                                key={url}
                                onClick={() => handleDownloadFile(url)}
                                title={ReactDOMServer.renderToString(
                                  <Translator
                                    file="module-legalCompliance"
                                    name="download.file"
                                  />
                                )}
                              >
                                {minions.files.fileIcon(
                                  url,
                                  true,
                                  null,
                                  'mr-2'
                                )}
                                {url}
                              </FileDownload>
                            ))
                          ) : (
                            <span>
                              <Translator
                                file="module-legalCompliance"
                                name="no.associated.files"
                              />
                            </span>
                          )}
                        </span>
                      </li>
                      <li>
                        <span className="eventinfo-title">
                          <Translator
                            file="user"
                            name="user.obligation.evidence.date"
                          />
                        </span>
                        <span className="eventinfo-content">
                          {selectedEvidence.date ? (
                            selectedEvidence.date
                          ) : (
                            <span className="text-muted">
                              <Translator file="user" name="user.no-date" />
                            </span>
                          )}
                        </span>
                      </li>
                      <li>
                        <span className="eventinfo-title">
                          <Translator
                            file="module-legalCompliance"
                            name="module.lc.evidence.description"
                          />
                        </span>
                        <span className="eventinfo-content">
                          {selectedEvidence.description}
                        </span>
                      </li>
                    </EvidenceInfo>
                  </>
                )}
              </>
            ) : (
              <AppNoInfo
                text={
                  <span>
                    <Translator
                      file="module-legalCompliance"
                      name="select.document.or.create.button"
                    />
                    <b>
                      {' '}
                      <Translator
                        file="module-legalCompliance"
                        name="module.lc.plant-evidence.new"
                      />
                    </b>
                    .
                  </span>
                }
                color={colors.primaryLight}
                icon={
                  <AppRemixIcon
                    size={40}
                    icon="file-search"
                    color={colors.primaryLight}
                  />
                }
              />
            )}
          </>
        )}
      </Content>
    </PlantEvidenceContainer>
  );
};
export default PlantEvidence;

const PlantEvidenceContainer = styled.div`
  display: grid;
  grid-template-columns: 350px 1fr;
`;

const SidebarMenu = styled.div`
  overflow-y: auto;
  padding: 0;

  display: flex;
  flex-direction: column;
  justify-content: flex-start;
  .add-button {
    display: flex;
    justify-content: center;
    align-items: center;
    border: 1px solid ${colors.primary};
    color: ${colors.primary};
    margin: 0;
    height: 32px;
    cursor: pointer;
  }
  ul {
    padding: 4px;
    //height: 268px;
    overflow-y: auto;
    li {
      padding: 4px 2px;
      border-bottom: 1px solid ${colors.grey300};
      cursor: pointer;
      &.ev-selected {
        background: ${colors.grey300};
      }
      .first-row {
        display: flex;
        justify-content: space-between;
        align-items: center;
        color: ${colors.primary};
        .name {
          display: -webkit-box;
          -webkit-line-clamp: 2;
          -webkit-box-orient: vertical;
          overflow: hidden;
          text-overflow: ellipsis;
        }
      }
      .second-row {
        display: flex;
        justify-content: space-between;
        align-items: center;
        font-size: 10px;
        .sr-date {
          letter-spacing: 1px;
        }
        .sr-user {
          color: ${colors.primary};
        }
      }
    }
  }
`;

const Content = styled.div`
  border-left: 1px solid ${colors.grey300};
  padding: 4px;
  height: 400px;
  .row-no-margin > .row {
    margin: 0;
  }
`;
const EvidenceFileList = styled.ul`
  list-style: none;
  margin: 0;
  padding-left: 6px;
  li {
    cursor: pointer;
    display: flex;
    align-items: center;
    border-top: 1px solid ${colors.grey300};
    padding: 8px 0;
    span {
      font-size: 12px;
      color: ${colors.primary};
    }
  }
  li:first-child {
    border-top: 0;
  }
`;

const NoFilesCard = styled.div`
  border: 1px solid ${colors.grey300};
`;

const EvidenceInfo = styled.ul`
  list-style: none;
  margin: 0;
  padding: 0;
  li {
    padding: 8px 0;
    border-top: 1px solid ${colors.grey300};
    display: flex;
    align-items: baseline;
    .eventinfo-title {
      display: inline-block;
      width: 200px;
      text-transform: uppercase;
      font-weight: bold;
      color: ${colors.primary};
    }
  }
`;

const FileDownload = styled.div`
  &:hover {
    cursor: pointer;
  }
`;
